import {
  crossesMidnight,
  type DurationInputMode,
  durationFromEndTime,
  endTimeFromDuration,
} from '@/shared/lib/end-time';
import { formatDuration, formatTime, type MinutesOfDay, parseTime } from '@/shared/lib/datetime';

import { DurationPicker } from './DurationPicker';
import { FieldShell, Input } from './Field';
import { SegmentedControl } from './SegmentedControl';

const INPUT_MODES: { value: DurationInputMode; label: string }[] = [
  { value: 'end', label: 'До времени' },
  { value: 'duration', label: 'Длительность' },
];

interface EndTimeFieldProps {
  startMin: MinutesOfDay;
  durationMin: number;
  onDurationChange: (durationMin: number) => void;
  mode: DurationInputMode;
  onModeChange: (mode: DurationInputMode) => void;
  /** Быстрый выбор длительности — у сна свои сроки, у встречи свои. */
  presets: readonly number[];
}

/**
 * Конец промежутка — временем окончания либо длительностью.
 *
 * Один и тот же промежуток удобно задавать по-разному: сон мыслится как «встаю
 * в семь», обед — как «сорок минут», встреча — как «с десяти до половины
 * двенадцатого». Оба способа меняют одну и ту же величину (длительность),
 * поэтому переключение ничего не сбрасывает, а сдвиг начала переносит
 * промежуток целиком.
 *
 * Поле общее для режима дня и встреч: расхождение форм ввода там, где правится
 * одно и то же, — источник ошибок.
 */
export function EndTimeField({
  startMin,
  durationMin,
  onDurationChange,
  mode,
  onModeChange,
  presets,
}: EndTimeFieldProps) {
  const endMin = endTimeFromDuration(startMin, durationMin);
  const nextDay = crossesMidnight(startMin, durationMin);

  return (
    <FieldShell
      label="Окончание"
      hint={
        mode === 'end'
          ? `Получится ${formatDuration(durationMin)}`
          : `Закончится в ${formatTime(endMin)}${nextDay ? ' следующего дня' : ''}`
      }
    >
      <SegmentedControl
        segments={INPUT_MODES}
        value={mode}
        onChange={onModeChange}
        className="mb-3"
      />

      {mode === 'end' ? (
        <Input
          type="time"
          aria-label="Время окончания"
          value={formatTime(endMin)}
          onChange={(event) => {
            // Пустое поле — незаконченный ввод, а не полночь.
            if (event.target.value === '') return;
            onDurationChange(durationFromEndTime(startMin, parseTime(event.target.value)));
          }}
          hint={nextDay ? 'Следующего дня' : undefined}
        />
      ) : (
        <DurationPicker presets={presets} value={durationMin} onChange={onDurationChange} />
      )}
    </FieldShell>
  );
}
